import React, { useMemo } from 'react';

import { FlexLayout } from '../Layout/Flex';
import { Thumbnail, ThumbnailProps } from '../Thumbnail';

type Props = {
  thumbnails: ThumbnailProps[];
  thumbnailLimit?: number;
  size?: ThumbnailProps['size'];
  direction?: 'ltr' | 'rtl';
};

const ThumbnailStack: React.FC<Props> = ({ thumbnails, thumbnailLimit = 5, size = 'md', direction = 'ltr' }) => {
  const spacing = useMemo(() => {
    switch (size) {
      case 'xxsm':
      case 'xsm':
        return 4;
      case 'sm':
        return 6;
      case 'md':
        return 8;
      case 'lg':
        return 12;
      case 'xlg':
      case 'xxlg':
      case 'xxxlg':
        return 16;
      case null:
        return 8;
      default:
        return 8;
    }
  }, [size]);

  const remainingThumbnails = useMemo(
    () => Math.max(thumbnails.length - thumbnailLimit, 0),
    [thumbnailLimit, thumbnails.length],
  );

  return (
    <FlexLayout className='relative'>
      {thumbnails
        .filter((_, i) => i < thumbnailLimit)
        .map((tn, index) => (
          <span
            key={`thumbnail_${index}_${tn.appearance}`}
            style={{
              zIndex: direction === 'ltr' && !remainingThumbnails ? thumbnails.length - index : index,
              marginLeft: `-${spacing}px`,
            }}
          >
            {React.createElement(Thumbnail, { ...tn, size })}
          </span>
        ))}
      {remainingThumbnails > 0 && (
        <span
          key='thumbnail_counter'
          style={{
            zIndex: direction === 'ltr' && !remainingThumbnails ? 0 : thumbnails.length,
            marginLeft: `-${spacing}px`,
          }}
        >
          <Thumbnail
            size={size}
            type='counter'
            bgColor='bg-color-background-info-subtle'
            appearance='circle'
            count={remainingThumbnails}
          />
        </span>
      )}
    </FlexLayout>
  );
};

export { ThumbnailStack };
